<template>
  <div class="franchise-application-section ptb-100" id="contact">
    <div class="container">
      <div class="row">
        <!-- Left Side - Information -->
        <div class="col-lg-5">
          <div class="franchise-info">
            <div class="section-header">
              <span class="badge">Start Your Journey</span>
              <h2>Join Our Growing Family</h2>
              <p>Take the first step toward owning your own Guac & Grill restaurant. Our streamlined application process makes it easy to get started.</p>
            </div>
            
            <div class="benefits-grid">
              <div class="benefit-card">
                <div class="benefit-icon">
                  <i class="ri-trophy-line"></i>
                </div>
                <div class="benefit-content">
                  <h4>Proven Success</h4>
                  <p>Join a brand with strong ROI and proven business model</p>
                </div>
              </div>
              
              <div class="benefit-card">
                <div class="benefit-icon">
                  <i class="ri-graduation-cap-line"></i>
                </div>
                <div class="benefit-content">
                  <h4>Complete Training</h4>
                  <p>Comprehensive training program and ongoing support</p>
                </div>
              </div>
              
              <div class="benefit-card">
                <div class="benefit-icon">
                  <i class="ri-rocket-line"></i>
                </div>
                <div class="benefit-content">
                  <h4>Marketing Support</h4>
                  <p>Full marketing and operational assistance included</p>
                </div>
              </div>
            </div>
            
            <div class="contact-info-card">
              <h4>Need Help?</h4>
              <p>Our franchise team is here to answer your questions</p>
              <div class="contact-methods">
  
                <a href="mailto:info@guacngrill.com" class="contact-method">
                  <i class="ri-mail-line"></i>
                  <span>info@guacngrill.com</span>
                </a>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Right Side - Application Form -->
        <div class="col-lg-7">
          <div class="application-form-container">
            <!-- Progress Steps -->
            <div class="form-progress">
              <div class="progress-steps">
                <div :class="['step', { active: currentStep >= 1, completed: currentStep > 1 }]">
                  <div class="step-number">1</div>
                  <span>Personal Info</span>
                </div>
                <div :class="['step', { active: currentStep >= 2, completed: currentStep > 2 }]">
                  <div class="step-number">2</div>
                  <span>Experience</span>
                </div>
                <div :class="['step', { active: currentStep >= 3, completed: currentStep > 3 }]">
                  <div class="step-number">3</div>
                  <span>Investment</span>
                </div>
                <div :class="['step', { active: currentStep >= 4 }]">
                  <div class="step-number">4</div>
                  <span>Submit</span>
                </div>
              </div>
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
              </div>
            </div>
            
            <form @submit.prevent="submitForm" class="modern-franchise-form">
              <!-- Step 1: Personal Information -->
              <div v-show="currentStep === 1" class="form-step">
                <div class="step-header">
                  <h3>Personal Information</h3>
                  <p>Tell us about yourself to get started</p>
                </div>
                
                <div class="form-grid">
                  <div class="input-group">
                    <label for="firstName">First Name *</label>
                    <input 
                      v-model="form.firstName" 
                      type="text" 
                      id="firstName"
                      :class="{ error: errors.firstName }"
                      placeholder="Enter your first name"
                    />
                    <span v-if="errors.firstName" class="error-text">{{ errors.firstName }}</span>
                  </div>
                  
                  <div class="input-group">
                    <label for="lastName">Last Name *</label>
                    <input 
                      v-model="form.lastName" 
                      type="text" 
                      id="lastName"
                      :class="{ error: errors.lastName }"
                      placeholder="Enter your last name"
                    />
                    <span v-if="errors.lastName" class="error-text">{{ errors.lastName }}</span>
                  </div>
                  
                  <div class="input-group full-width">
                    <label for="email">Email Address *</label>
                    <input 
                      v-model="form.email" 
                      type="email" 
                      id="email"
                      :class="{ error: errors.email }"
                      placeholder="your.email@example.com"
                    />
                    <span v-if="errors.email" class="error-text">{{ errors.email }}</span>
                  </div>
                  
                  <div class="input-group full-width">
                    <label for="phone">Phone Number *</label>
                    <input 
                      v-model="form.phone" 
                      type="tel" 
                      id="phone"
                      :class="{ error: errors.phone }"
                      placeholder="+1 (555) 123-4567"
                    />
                    <span v-if="errors.phone" class="error-text">{{ errors.phone }}</span>
                  </div>
                  
                  <div class="input-group full-width">
                    <label for="city">Preferred Location *</label>
                    <input 
                      v-model="form.city" 
                      type="text" 
                      id="city"
                      :class="{ error: errors.city }"
                      placeholder="City, State where you'd like to open"
                    />
                    <span v-if="errors.city" class="error-text">{{ errors.city }}</span>
                  </div>
                </div>
                
                <div class="step-actions">
                  <button type="button" @click="nextStep" class="btn-next">
                    Continue <i class="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
              
              <!-- Step 2: Experience -->
              <div v-show="currentStep === 2" class="form-step">
                <div class="step-header">
                  <h3>Your Experience</h3>
                  <p>Help us understand your background</p>
                </div>
                
                <div class="form-grid">
                  <div class="input-group full-width">
                    <label for="experience">Restaurant Experience *</label>
                    <div class="select-wrapper">
                      <select v-model="form.experience" id="experience" :class="{ error: errors.experience }">
                        <option value="">Select your experience level</option>
                        <option value="none">No Restaurant Experience</option>
                        <option value="1-3">1-3 Years Experience</option>
                        <option value="3-5">3-5 Years Experience</option>
                        <option value="5-10">5-10 Years Experience</option>
                        <option value="10+">10+ Years Experience</option>
                      </select>
                      <i class="ri-arrow-down-s-line"></i>
                    </div>
                    <span v-if="errors.experience" class="error-text">{{ errors.experience }}</span>
                  </div>
                  
                  <div class="input-group full-width">
                    <label for="message">Why Guac & Grill?</label>
                    <textarea 
                      v-model="form.message" 
                      id="message"
                      rows="4"
                      placeholder="Tell us why you're interested in a Guac & Grill franchise..."
                    ></textarea>
                  </div>
                </div>
                
                <div class="step-actions">
                  <button type="button" @click="prevStep" class="btn-prev">
                    <i class="ri-arrow-left-line"></i> Back
                  </button>
                  <button type="button" @click="nextStep" class="btn-next">
                    Continue <i class="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
              
              <!-- Step 3: Investment -->
              <div v-show="currentStep === 3" class="form-step">
                <div class="step-header">
                  <h3>Investment Details</h3>
                  <p>Let's discuss your investment capacity</p>
                </div>
                
                <div class="form-grid">
                  <div class="input-group full-width">
                    <label for="investment">Available Investment *</label>
                    <div class="select-wrapper">
                      <select v-model="form.investment" id="investment" :class="{ error: errors.investment }">
                        <option value="">Select your investment range</option>
                        <option value="250k-500k">$250,000 - $500,000</option>
                        <option value="500k-750k">$500,000 - $750,000</option>
                        <option value="750k-1m">$750,000 - $1,000,000</option>
                        <option value="1m-1.5m">$1,000,000 - $1,500,000</option>
                        <option value="1.5m+">$1,500,000+</option>
                      </select>
                      <i class="ri-arrow-down-s-line"></i>
                    </div>
                    <span v-if="errors.investment" class="error-text">{{ errors.investment }}</span>
                  </div>
                  
                  <div class="investment-info">
                    <div class="info-card">
                      <h4>Investment Breakdown</h4>
                      <ul>
                        <li><span>Initial Franchise Fee:</span> <strong>$45,000</strong></li>
                        <li><span>Equipment & Setup:</span> <strong>$150,000 - $250,000</strong></li>
                        <li><span>Working Capital:</span> <strong>$50,000 - $100,000</strong></li>
                        <li><span>Marketing Launch:</span> <strong>$15,000 - $25,000</strong></li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div class="step-actions">
                  <button type="button" @click="prevStep" class="btn-prev">
                    <i class="ri-arrow-left-line"></i> Back
                  </button>
                  <button type="button" @click="nextStep" class="btn-next">
                    Continue <i class="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
              
              <!-- Step 4: Review & Submit -->
              <div v-show="currentStep === 4" class="form-step">
                <div class="step-header">
                  <h3>Review & Submit</h3>
                  <p>Please review your information before submitting</p>
                </div>
                
                <div class="review-section">
                  <div class="review-card">
                    <h4>Personal Information</h4>
                    <div class="review-grid">
                      <div><strong>Name:</strong> {{ form.firstName }} {{ form.lastName }}</div>
                      <div><strong>Email:</strong> {{ form.email }}</div>
                      <div><strong>Phone:</strong> {{ form.phone }}</div>
                      <div><strong>Location:</strong> {{ form.city }}</div>
                    </div>
                    <button type="button" @click="currentStep = 1" class="edit-btn">Edit</button>
                  </div>
                  
                  <div class="review-card">
                    <h4>Experience & Interest</h4>
                    <div class="review-grid">
                      <div><strong>Experience:</strong> {{ getExperienceLabel(form.experience) }}</div>
                      <div v-if="form.message"><strong>Message:</strong> {{ form.message }}</div>
                    </div>
                    <button type="button" @click="currentStep = 2" class="edit-btn">Edit</button>
                  </div>
                  
                  <div class="review-card">
                    <h4>Investment</h4>
                    <div class="review-grid">
                      <div><strong>Range:</strong> {{ getInvestmentLabel(form.investment) }}</div>
                    </div>
                    <button type="button" @click="currentStep = 3" class="edit-btn">Edit</button>
                  </div>
                </div>
                
                <div class="terms-section">
                  <label class="checkbox-container">
                    <input v-model="form.agreeTerms" type="checkbox" required />
                    <span class="checkmark"></span>
                    <span class="checkbox-text">
                      I agree to be contacted by Guac & Grill franchise team and accept the 
                      <a href="/terms-conditions" target="_blank">terms & conditions</a> *
                    </span>
                  </label>
                  <span v-if="errors.agreeTerms" class="error-text">{{ errors.agreeTerms }}</span>
                </div>
                
                <div class="step-actions">
                  <button type="button" @click="prevStep" class="btn-prev">
                    <i class="ri-arrow-left-line"></i> Back
                  </button>
                  <button type="submit" :disabled="isSubmitting" class="btn-submit">
                    <span v-if="!isSubmitting">
                      <i class="ri-send-plane-line"></i>
                      Submit Application
                    </span>
                    <span v-else>
                      <i class="ri-loader-4-line spinning"></i>
                      Submitting...
                    </span>
                  </button>
                </div>
              </div>
              
              <!-- Success/Error Message -->
              <div v-if="submitMessage.type" :class="['submit-message', submitMessage.type]">
                <i :class="submitMessage.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'"></i>
                <div>
                  <h4>{{ submitMessage.type === 'success' ? 'Application Submitted!' : 'Submission Error' }}</h4>
                  <p>{{ submitMessage.text }}</p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, ref, computed } from "vue";

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  experience: string;
  investment: string;
  message: string;
  agreeTerms: boolean;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  city?: string;
  experience?: string;
  investment?: string;
  agreeTerms?: string;
}

export default defineComponent({
  name: "FranchiseContact",
  setup() {
    const currentStep = ref(1);
    const isSubmitting = ref(false);
    
    const form = reactive<FormData>({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      city: '',
      experience: '',
      investment: '',
      message: '',
      agreeTerms: false
    });

    const errors = reactive<FormErrors>({});
    const submitMessage = reactive<{ type: 'success' | 'error' | null; text: string }>({
      type: null,
      text: ''
    });

    const progressPercentage = computed(() => {
      return ((currentStep.value - 1) / 3) * 100;
    });

    const validateStep = (step: number): boolean => {
      const newErrors: FormErrors = {};

      if (step === 1) {
        if (!form.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!form.lastName.trim()) newErrors.lastName = 'Last name is required';
        
        if (!form.email.trim()) {
          newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
          newErrors.email = 'Please enter a valid email';
        }
        
        if (!form.phone.trim()) {
          newErrors.phone = 'Phone number is required';
        } else if (!/^[\d\s\-+()]+$/.test(form.phone)) {
          newErrors.phone = 'Please enter a valid phone number';
        }
        
        if (!form.city.trim()) newErrors.city = 'City/Location is required';
      }

      if (step === 2) {
        if (!form.experience) newErrors.experience = 'Please select your experience level';
      }

      if (step === 3) {
        if (!form.investment) newErrors.investment = 'Please select your investment range';
      }

      if (step === 4) {
        if (!form.agreeTerms) newErrors.agreeTerms = 'You must agree to the terms and conditions';
      }

      // Clear previous errors and set new ones
      Object.keys(errors).forEach(key => delete errors[key as keyof FormErrors]);
      Object.assign(errors, newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const nextStep = () => {
      if (validateStep(currentStep.value)) {
        if (currentStep.value < 4) {
          currentStep.value++;
        }
      }
    };

    const prevStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--;
      }
    };

    const getExperienceLabel = (value: string): string => {
      const labels: { [key: string]: string } = {
        'none': 'No Restaurant Experience',
        '1-3': '1-3 Years Experience',
        '3-5': '3-5 Years Experience',
        '5-10': '5-10 Years Experience',
        '10+': '10+ Years Experience'
      };
      return labels[value] || value;
    };

    const getInvestmentLabel = (value: string): string => {
      const labels: { [key: string]: string } = {
        '250k-500k': '$250,000 - $500,000',
        '500k-750k': '$500,000 - $750,000',
        '750k-1m': '$750,000 - $1,000,000',
        '1m-1.5m': '$1,000,000 - $1,500,000',
        '1.5m+': '$1,500,000+'
      };
      return labels[value] || value;
    };

    const submitForm = async () => {
      // Clear previous errors and message
      Object.keys(errors).forEach(key => delete errors[key as keyof FormErrors]);
      submitMessage.type = null;
      submitMessage.text = '';

      // Validate all steps
      let allValid = true;
      for (let step = 1; step <= 4; step++) {
        if (!validateStep(step)) {
          allValid = false;
          currentStep.value = step;
          break;
        }
      }

      if (!allValid) {
        return;
      }

      isSubmitting.value = true;

      try {
        // Simulate API call - replace with actual endpoint
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Here you would normally send the data to your backend
        console.log('Franchise Application Submitted:', form);
        
        submitMessage.type = 'success';
        submitMessage.text = 'Thank you for your interest! Our franchise team will contact you within 24-48 hours to discuss the next steps.';
        
        // Reset form
        Object.assign(form, {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          city: '',
          experience: '',
          investment: '',
          message: '',
          agreeTerms: false
        });
        
        currentStep.value = 1;
        
      } catch (error) {
        console.error('Submission error:', error);
        submitMessage.type = 'error';
        submitMessage.text = 'An error occurred while submitting your application. Please try again or contact us directly.';
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      currentStep,
      form,
      errors,
      isSubmitting,
      submitMessage,
      progressPercentage,
      nextStep,
      prevStep,
      getExperienceLabel,
      getInvestmentLabel,
      submitForm
    };
  }
});
</script>

<style lang="scss" scoped>
.franchise-application-section {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
  min-height: 100vh;
}

.franchise-info {
  .section-header {
    .badge {
      background: linear-gradient(135deg, #b5ff00 0%, #9ee600 100%);
      color: #000000;
      padding: 0.5rem 1rem;
      border-radius: 2rem;
      font-size: 0.875rem;
      font-weight: 600;
      margin-bottom: 1rem;
      display: inline-block;
    }
    
    h2 {
      color: #f8fafc;
      font-size: 2.5rem;
      font-weight: 800;
      margin-bottom: 1rem;
    }
    
    p {
      color: #cbd5e1;
      font-size: 1.125rem;
      line-height: 1.7;
    }
  }
}

.benefits-grid {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin: 3rem 0;
  
  .benefit-card {
    display: flex;
    gap: 1rem;
    padding: 1.5rem;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 1rem;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.08);
      transform: translateY(-2px);
    }
    
    .benefit-icon {
      width: 3rem;
      height: 3rem;
      background: linear-gradient(135deg, #b5ff00 0%, #9ee600 100%);
      border-radius: 0.75rem;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      
      i {
        font-size: 1.5rem;
        color: #000000;
      }
    }
    
    .benefit-content {
      h4 {
        color: #f8fafc;
        font-size: 1.125rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
      }
      
      p {
        color: #cbd5e1;
        font-size: 0.875rem;
        margin: 0;
      }
    }
  }
}

.contact-info-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  padding: 1.5rem;
  
  h4 {
    color: #f8fafc;
    margin-bottom: 0.5rem;
  }
  
  p {
    color: #cbd5e1;
    margin-bottom: 1rem;
  }
  
  .contact-methods {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    
    .contact-method {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      color: #cbd5e1;
      text-decoration: none;
      padding: 0.75rem;
      border-radius: 0.5rem;
      transition: all 0.3s ease;
      
      &:hover {
        background: rgba(255, 255, 255, 0.05);
        color: #b5ff00;
      }
      
      i {
        color: #b5ff00;
      }
    }
  }
}

.application-form-container {
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
}

.form-progress {
  margin-bottom: 2.5rem;
  
  .progress-steps {
    display: flex;
    justify-content: space-between;
    margin-bottom: 1rem;
    
    .step {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
      flex: 1;
      position: relative;
      
      &:not(:last-child)::after {
        content: '';
        position: absolute;
        top: 1rem;
        left: 60%;
        right: -40%;
        height: 2px;
        background: rgba(255, 255, 255, 0.1);
      }
      
      &.completed:not(:last-child)::after {
        background: #b5ff00;
      }
      
      .step-number {
        width: 2rem;
        height: 2rem;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.1);
        color: #64748b;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        transition: all 0.3s ease;
        position: relative;
        z-index: 1;
      }
      
      span {
        font-size: 0.75rem;
        color: #64748b;
        font-weight: 500;
        transition: all 0.3s ease;
      }
      
      &.active {
        .step-number {
          background: linear-gradient(135deg, #b5ff00 0%, #9ee600 100%);
          color: #000000;
          box-shadow: 0 0 0 4px rgba(181, 255, 0, 0.2);
        }
        
        span {
          color: #b5ff00;
          font-weight: 600;
        }
      }
      
      &.completed {
        .step-number {
          background: linear-gradient(135deg, #b5ff00 0%, #9ee600 100%);
          color: #000000;
        }
        
        span {
          color: #cbd5e1;
        }
      }
    }
  }
  
  .progress-bar {
    height: 4px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
    overflow: hidden;
    
    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #b5ff00 0%, #9ee600 100%);
      transition: width 0.5s ease;
    }
  }
}

.modern-franchise-form {
  .form-step {
    .step-header {
      text-align: center;
      margin-bottom: 2rem;
      
      h3 {
        color: #f8fafc;
        font-size: 1.5rem;
        font-weight: 700;
        margin-bottom: 0.5rem;
      }
      
      p {
        color: #cbd5e1;
        font-size: 0.875rem;
      }
    }
    
    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1.5rem;
      margin-bottom: 2rem;
      
      .input-group {
        &.full-width {
          grid-column: 1 / -1;
        }
        
        label {
          display: block;
          color: #e2e8f0;
          font-size: 0.875rem;
          font-weight: 500;
          margin-bottom: 0.5rem;
        }
        
        input, textarea {
          width: 100%;
          background: rgba(255, 255, 255, 0.05);
          border: 2px solid rgba(255, 255, 255, 0.1);
          border-radius: 0.75rem;
          color: #f8fafc;
          font-size: 1rem;
          padding: 0.875rem 1rem;
          transition: all 0.3s ease;
          
          &::placeholder {
            color: #64748b;
          }
          
          &:focus {
            outline: none;
            border-color: #b5ff00;
            background: rgba(255, 255, 255, 0.08);
            box-shadow: 0 0 0 3px rgba(181, 255, 0, 0.1);
          }
          
          &.error {
            border-color: #ef4444;
            background: rgba(239, 68, 68, 0.05);
          }
        }
        
        textarea {
          resize: vertical;
          min-height: 100px;
        }
        
        .select-wrapper {
          position: relative;
          
          select {
            width: 100%;
            background: rgba(255, 255, 255, 0.05);
            border: 2px solid rgba(255, 255, 255, 0.1);
            border-radius: 0.75rem;
            color: #f8fafc;
            font-size: 1rem;
            padding: 0.875rem 2.5rem 0.875rem 1rem;
            cursor: pointer;
            appearance: none;
            transition: all 0.3s ease;
            
            &:focus {
              outline: none;
              border-color: #b5ff00;
              background: rgba(255, 255, 255, 0.08);
              box-shadow: 0 0 0 3px rgba(181, 255, 0, 0.1);
            }
            
            &.error {
              border-color: #ef4444;
              background: rgba(239, 68, 68, 0.05);
            }
            
            option {
              background: #1e293b;
              color: #f8fafc;
            }
          }
          
          i {
            position: absolute;
            right: 1rem;
            top: 50%;
            transform: translateY(-50%);
            color: #64748b;
            pointer-events: none;
          }
        }
        
        .error-text {
          color: #ef4444;
          font-size: 0.75rem;
          margin-top: 0.25rem;
          
          &::before {
            content: "⚠ ";
          }
        }
      }
    }
    
    .investment-info .info-card {
      background: rgba(181, 255, 0, 0.05);
      border: 1px solid rgba(181, 255, 0, 0.2);
      border-radius: 0.75rem;
      padding: 1.5rem;
      margin-top: 1.5rem;
      
      h4 {
        color: #b5ff00;
        margin-bottom: 1rem;
      }
      
      ul {
        list-style: none;
        padding: 0;
        margin: 0;
        
        li {
          display: flex;
          justify-content: space-between;
          padding: 0.5rem 0;
          border-bottom: 1px solid rgba(181, 255, 0, 0.1);
          
          &:last-child {
            border-bottom: none;
          }
          
          span {
            color: #cbd5e1;
          }
          
          strong {
            color: #b5ff00;
          }
        }
      }
    }
    
    .review-section {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin-bottom: 2rem;
      
      .review-card {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 0.75rem;
        padding: 1.5rem;
        position: relative;
        
        h4 {
          color: #f8fafc;
          margin-bottom: 1rem;
        }
        
        .review-grid {
          display: grid;
          gap: 0.5rem;
          
          div {
            color: #cbd5e1;
            font-size: 0.875rem;
            
            strong {
              color: #e2e8f0;
            }
          }
        }
        
        .edit-btn {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: rgba(181, 255, 0, 0.1);
          color: #b5ff00;
          border: 1px solid rgba(181, 255, 0, 0.2);
          border-radius: 0.5rem;
          padding: 0.25rem 0.75rem;
          font-size: 0.75rem;
          cursor: pointer;
          
          &:hover {
            background: rgba(181, 255, 0, 0.2);
          }
        }
      }
    }
    
    .terms-section {
      margin-bottom: 2rem;
      
      .checkbox-container {
        display: flex;
        gap: 0.75rem;
        cursor: pointer;
        
        input[type="checkbox"] {
          display: none;
        }
        
        .checkmark {
          width: 1.25rem;
          height: 1.25rem;
          background: rgba(255, 255, 255, 0.05);
          border: 2px solid rgba(255, 255, 255, 0.2);
          border-radius: 0.375rem;
          position: relative;
          flex-shrink: 0;
          margin-top: 0.125rem;
          
          &::after {
            content: "";
            position: absolute;
            left: 0.25rem;
            top: 0.125rem;
            width: 0.25rem;
            height: 0.5rem;
            border: solid white;
            border-width: 0 2px 2px 0;
            transform: rotate(45deg) scale(0);
            transition: transform 0.2s ease;
          }
        }
        
        input[type="checkbox"]:checked + .checkmark {
          background: #b5ff00;
          border-color: #b5ff00;
          
          &::after {
            transform: rotate(45deg) scale(1);
          }
        }
        
        .checkbox-text {
          color: #cbd5e1;
          font-size: 0.875rem;
          
          a {
            color: #b5ff00;
            text-decoration: none;
            
            &:hover {
              text-decoration: underline;
            }
          }
        }
      }
    }
    
    .step-actions {
      display: flex;
      justify-content: space-between;
      gap: 1rem;
      
      .btn-prev, .btn-next, .btn-submit {
        padding: 0.875rem 1.5rem;
        border-radius: 0.75rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        border: none;
      }
      
      .btn-prev {
        background: rgba(255, 255, 255, 0.1);
        color: #cbd5e1;
        border: 1px solid rgba(255, 255, 255, 0.2);
        
        &:hover {
          background: rgba(255, 255, 255, 0.15);
        }
      }
      
      .btn-next, .btn-submit {
        background: linear-gradient(135deg, #b5ff00 0%, #9ee600 100%);
        color: #000000;
        box-shadow: 0 4px 12px rgba(181, 255, 0, 0.3);
        
        &:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(181, 255, 0, 0.4);
        }
        
        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }
      
      .btn-submit {
        padding: 1rem 2rem;
        
        .spinning {
          animation: spin 1s linear infinite;
        }
      }
    }
  }
  
  .submit-message {
    margin-top: 2rem;
    padding: 1.5rem;
    border-radius: 0.75rem;
    display: flex;
    gap: 1rem;
    
    i {
      font-size: 1.5rem;
      flex-shrink: 0;
    }
    
    h4 {
      font-weight: 600;
      margin-bottom: 0.25rem;
    }
    
    p {
      margin: 0;
    }
    
    &.success {
      background: rgba(181, 255, 0, 0.1);
      border: 1px solid rgba(181, 255, 0, 0.2);
      
      i, h4 {
        color: #b5ff00;
      }
      
      p {
        color: #9ee600;
      }
    }
    
    &.error {
      background: rgba(239, 68, 68, 0.1);
      border: 1px solid rgba(239, 68, 68, 0.2);
      
      i, h4 {
        color: #ef4444;
      }
      
      p {
        color: #dc2626;
      }
    }
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .franchise-info {
    margin-bottom: 2rem;
    
    .section-header h2 {
      font-size: 2rem;
    }
  }
  
  .application-form-container {
    padding: 1.5rem;
  }
  
  .modern-franchise-form .form-step {
    .form-grid {
      grid-template-columns: 1fr;
      
      .input-group.full-width {
        grid-column: 1;
      }
    }
    
    .step-actions {
      flex-direction: column;
      
      .btn-prev, .btn-next, .btn-submit {
        width: 100%;
        justify-content: center;
      }
    }
  }
}
</style>
